import {type JSONData} from "../../base_classes/dto.d";
import {AirlineOrderGroupingDto} from "./dto.gen";
import {type IAirlineOrderGrouping} from "./entity.d";
import AirlineOrderGroupingParsers from "./parsers";
import AirlineOrderGroupingValidators from "./custom_validators";
import {AirlineOrder, AirlineOrderGroup, AirlinePricing} from "..";
import request from "@/data/api/request";
import {toast} from "vue3-toastify";

export class AirlineOrderGrouping
  extends AirlineOrderGroupingDto
  implements IAirlineOrderGrouping {

  /**
   * Process the orders to obtain the optimal order groups
   * @returns {{ afasOrders: any[], afasOrderIds: string[][] }} The afas orders and their ID's
   */
  toAFASOrders(): {
    afasOrders: any[];
    afasOrderIds: string[][];
  } {
    const afasOrders: any[] = [];
    const afasOrderIds: string[][] = [];

    // Loop through order groups
    for (const orderGroup of this.orderGroups) {
      if (orderGroup.orders) {
        // Get unique delivery address code
        const deliveryAddressCodes =
          AirlineOrderGroup.getDeliveryAddressCodes(orderGroup as any);

        // Loop through delivery address codes
        for (const deliveryAddressCode of deliveryAddressCodes) {
          // Get orders to deliver at this address
          const { orders, orderIds } =
            AirlineOrder.selectOrderAndIdsByDeliveryAddressCode(
              orderGroup.orders as any,
              deliveryAddressCode,
            );

          // Create AFAS order (Simplified for client-side preview)
          const afasOrder = {
            name: this.name +
              " - " +
              orderGroup.name +
              " - " +
              (deliveryAddressCodes.indexOf(deliveryAddressCode) + 1),
            deliveryAddressCode,
            supplierId: orders[0]?.supplierId,
            warehouse: orders[0]?.warehouse,
            reference: orders[0]?.warehouse === "03" ? orders[0]?.number : "1000001",
            date: new Date().toISOString().split("T")[0],
            unit: 1,
            orders: orders.map(o => o.toJSON()),
          };

          // Push to orders
          afasOrders.push(afasOrder);
          afasOrderIds.push(orderIds);
        }
      }
    }

    return {
      afasOrders,
      afasOrderIds,
    };
  }

  constructor(
    pathVars?:
      | string
      | string[]
      | {
      id?: string;
    },
    data?: JSONData,
  ) {
    super(data, AirlineOrderGroupingParsers, AirlineOrderGroupingValidators);

    if (data !== undefined) {
      // Set data
      this.set(data);
    }

    this.buildPathObject(pathVars);
  }




  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  /**
   * Override set to deserialize invalidOrders
   */
  override set(data: JSONData): void {
    // Deserialize invalidOrders from JSON string if needed
    if (data.invalidOrders && typeof data.invalidOrders === 'string') {
      console.log("AirlineOrderGrouping.set: invalidOrders is a string, deserializing...");
      try {
        data.invalidOrders = JSON.parse(data.invalidOrders);
      } catch (e) {
        console.error('Failed to deserialize invalidOrders in set():', e);
        data.invalidOrders = [];
      }
    }

    // Call parent set - this will handle orderGroups deserialization via AirlineOrderGroup.set()
    super.set(data);
  }


  public async get(
    pathVars?:
      | string
      | string[]
      | {
      id?: string;
    }
      | undefined,
  ): Promise<AirlineOrderGrouping> {
    const result = (await super.get(pathVars)) as AirlineOrderGrouping;

    console.log("=== AFTER API GET ===");
    console.log("OrderGroups:", result.orderGroups);
    if (result.orderGroups && result.orderGroups.length > 0) {
      console.log("First group orders type:", typeof result.orderGroups[0].orders);
      console.log("First group orders is array?:", Array.isArray(result.orderGroups[0].orders));
      console.log("First group orders length:", result.orderGroups[0].orders?.length);
      console.log("First group units:", result.orderGroups[0].units);
      console.log("First group maxWidth:", result.orderGroups[0].maxWidth);
    }

    return result;
  }

  /**
   * Deserialize JSON strings back to nested arrays
   * This reverses the serialization done in toDatastoreJSON on the backend
   * @returns {void}
   */
  deserializeNestedArrays(): void {
    console.log("=== DESERIALIZE NESTED ARRAYS CALLED ===");

    // Deserialize invalidOrders from JSON string
    if (typeof this.invalidOrders === 'string') {
      console.log("Deserializing invalidOrders from string");
      try {
        const parsed = JSON.parse(this.invalidOrders as any);
        this.invalidOrders = AirlineOrder.FromJSONArray(parsed);
      } catch (e) {
        console.error('Failed to deserialize invalidOrders:', e);
        this.invalidOrders = [];
      }
    }

    // Deserialize nested arrays in orderGroups
    if (this.orderGroups && Array.isArray(this.orderGroups)) {
      console.log("Deserializing orderGroups, count:", this.orderGroups.length);

      this.orderGroups = this.orderGroups.map((group: any, index: number) => {
        console.log(`Processing group ${index + 1}:`, group);

        // Deserialize orders from JSON string
        if (typeof group.orders === 'string') {
          console.log(`  Group ${index + 1}: orders IS a string, parsing...`);
          try {
            const parsed = JSON.parse(group.orders);
            console.log(`  Group ${index + 1}: parsed orders:`, parsed);
            group.orders = AirlineOrder.FromJSONArray(parsed);
            console.log(`  Group ${index + 1}: orders after FromJSONArray:`, group.orders);
          } catch (e) {
            console.error(`Failed to deserialize orders in group ${index + 1}:`, e);
            group.orders = [];
          }
        } else {
          console.log(`  Group ${index + 1}: orders is NOT a string, type:`, typeof group.orders, "value:", group.orders);
        }

        // Deserialize pricingTables from JSON string
        if (typeof group.pricingTables === 'string') {
          try {
            const parsed = JSON.parse(group.pricingTables);
            group.pricingTables = AirlinePricing.FromJSONArray(parsed);
          } catch (e) {
            console.error('Failed to deserialize pricingTables in group:', e);
            group.pricingTables = [];
          }
        }

        // Deserialize settings from JSON string
        if (typeof group.settings === 'string') {
          try {
            group.settings = JSON.parse(group.settings);
          } catch (e) {
            console.error('Failed to deserialize settings in group:', e);
            group.settings = {};
          }
        }

        return group;
      });
    } else {
      console.log("OrderGroups is not an array or is null/undefined");
    }

    console.log("=== DESERIALIZATION COMPLETE ===");
  }


  get firstDesiredDeliveryDateCalculated(): string | null {

    let earliestDate: Date | null = null;

    for (const group of this.orderGroups) {
      if (!group.orders) continue;
      for (const order of group.orders) {
        if (order.desiredDeliveryDate) {
          const date = new Date(order.desiredDeliveryDate);
          if (!isNaN(date.getTime())) {
            if (!earliestDate || date < earliestDate) {
              earliestDate = date;
            }
          }
        }
      }
    }

    return earliestDate ? earliestDate.toISOString() : null;
  }
  
  get purchaseDeadlineDateCalculated(): string | null {

      const firstDate = this.firstDesiredDeliveryDateCalculated;

      if (!firstDate) {
        return null;
      }

      const date = new Date(firstDate);
      // Subtract 6 weeks (42 days)
      date.setDate(date.getDate() - 42);
    
      return date.toISOString();
    }


  get arrayBaseFoilSkuWithAmountOfRolls() {
    const baseFoilSkus = new Map<string, number>();

    for (const group of this.orderGroups) {
      for (const order of group.orders) {
        if (order.baseFoilSKU && order.baseFoilRollAmount) {
          const currentAmount = baseFoilSkus.get(order.baseFoilSKU) || 0;
          baseFoilSkus.set(order.baseFoilSKU, currentAmount + order.baseFoilRollAmount);
        }
      }
    }

    console.log(baseFoilSkus);
    return baseFoilSkus;
  }


  /*
   * Calculate the order grouping on the server side
   */
  async build(orders: AirlineOrder[]) {
    try {
      // Calculate server side
      const data = await request({
        url: "/airline_order/calculate_optimal_order_grouping",
        method: "POST",
        data: {
          items: orders.map((order) => order.toJSON()),
        },
      });

      // Handle null response (e.g., from 401 errors)
      if (data === null) {
        console.info("Request returned null, likely due to authentication error");
        return this;
      }

      // Unpack
      const {item}: { item: JSONData } = data;

      // Exists
      if (typeof item !== "object") {
        throw new Error("Incorrect data");
      } else if (item === null) {
        console.info("No item found");
        return this;
      }

      // Parse data
      const parsedData = this._parser.parse(item);

      // Get data
      this.set(parsedData);
    } catch (error: any) {
      // Handle API errors
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Check for pricing table error specifically
        if (errorData.status === "error" &&
          errorData.message &&
          errorData.message.includes("Pricing table not found")) {
          toast.error(errorData.message + ". Please make sure the pricing table is available in the system and try again.",
            {
              autoClose: 10000,
            }
            );
        } else {
          // Handle other API errors
          const errorMessage = errorData.message || "An error occurred while calculating the order grouping";
          toast.error(errorMessage);
        }
      } else {
        // Handle network or other errors
        toast.error("Failed to calculate order grouping. Please try again.");
      }

      console.error("Error in build method:", error);
      throw error;
    }
  }

  /**
   * Set the pricing tables on the order groups
   * @param data The pricing tables
   * @returns {void}
   */
  setPricingTables(pricingTables: AirlinePricing[]): void {
    this.orderGroups.forEach((group) => {
      group.setPricingTables(pricingTables);
    });
  }

  /**
   * Send the order grouping to Afas
   * @returns
   */
  sendToAfas(): Promise<void> {
    return request({
      url: "/airline_order_grouping/" + this.id + "/send_to_afas",
      method: "POST",
    });
  }

  /// ///////////////////////////////////////
  /// /////////// STATIC METHODS ////////////
  /// ///////////////////////////////////////

  static override FromJSON(data?: JSONData): AirlineOrderGrouping {
    return new AirlineOrderGrouping(undefined, data);
  }

  static override FromJSONArray(data: JSONData[]): AirlineOrderGrouping[] {
    return data.map((item) => AirlineOrderGrouping.FromJSON(item));
  }

  static FromDto(dto: AirlineOrderGroupingDto): AirlineOrderGrouping {
    return new AirlineOrderGrouping(undefined, dto.toJSON());
  }
}
